// v3 value story snapshot: the denormalised, math-free record of one sizing, and the boundary
// between the calculator and the deck / Salesforce Story_JSON__c.
//
// `v: 2` — v1 is the v2-calculator snapshot (types.ts StorySnapshot). A rollback that only knows v1
// can refuse a v2 story instead of misreading it (Q58). Built alongside v2's buildStorySnapshot;
// the persistence stage swaps it in. Zod schemas because this JSON crosses the SFDC boundary.

import { getSizedUseCase, type SizedUseCase } from '../data/use-case-catalog';
import {
  VALUE_STORY_VERSION,
  type StoryInput,
  type StoryProof,
  type StoryUseCase,
  type ValueStory,
} from './value-story-schema';
import {
  computeCalculator,
  enabledLevels,
  enabledMethods,
  orderSelection,
  resolveUseCaseAnswers,
  type CalculatorInput,
  type UseCaseSelection,
} from './use-case-engine';

export {
  StoryFormulaStepSchema,
  StoryInputSchema,
  StoryMetricSchema,
  StoryProofSchema,
  StorySectionSchema,
  StorySubTotalSchema,
  StoryUseCaseSchema,
  VALUE_STORY_VERSION,
  ValueStorySchema,
  type StoryFormulaStep,
  type StoryInput,
  type StoryMetric,
  type StoryProof,
  type StoryUseCase,
  type ValueStory,
} from './value-story-schema';

export interface ValueStoryInput extends CalculatorInput {
  /** Injected so the builder stays pure. */
  created: number;
  customerName: string;
  /** Top benchmark rows per use case id, flattened by the caller. */
  stories?: Record<string, StoryProof[]> | null;
}

function inputsFor(uc: SizedUseCase, sel: UseCaseSelection, resolved: Record<string, number>): StoryInput[] {
  const saved = sel.answers ?? {};
  return uc.questions
    .filter((q) => q.key in resolved)
    .map((q) => ({
      key: q.key,
      label: q.label,
      role: q.role,
      kind: q.kind,
      recordLevel: q.recordLevel ?? null,
      value: resolved[q.key],
      assumed: saved[q.key] === undefined,
    }));
}

/** Build the v3 snapshot from calculator state. Pure; the result always satisfies ValueStorySchema. */
export function buildValueStory(input: ValueStoryInput): ValueStory {
  const { rollup, useCases } = computeCalculator(input);
  const sels = new Map(orderSelection(input.selected).map((s) => [s.id, s]));
  const entries: StoryUseCase[] = useCases.map((r, i) => {
    const uc = getSizedUseCase(r.id) as SizedUseCase;
    const sel = sels.get(r.id) as UseCaseSelection;
    const line = rollup.lines[i];
    const resolved = resolveUseCaseAnswers(
      uc,
      sel.answers,
      enabledLevels(uc, sel.recordLevels),
      input.deal.acv,
      enabledMethods(uc, sel.methods),
    );
    return {
      id: uc.id,
      name: uc.name,
      tier: uc.tier,
      gridRow: uc.gridRow,
      confidence: uc.confidence,
      outcome: uc.outcome,
      path: uc.path,
      benchmarkClaim: uc.benchmarkClaim.text,
      recordLevels: r.recordLevels,
      ...(r.methods ? { methods: r.methods } : {}),
      value: r.value,
      realizationPct: line.realizationPct,
      programValue: line.programValue,
      attributionPct: line.attributionPct,
      attributedValue: line.attributedValue,
      pipeline: r.pipeline,
      sections: r.sections,
      metrics: r.metrics,
      inputs: inputsFor(uc, sel, resolved),
      notes: r.notes,
      displacedTools: r.displacedTools,
      stories: (input.stories ?? {})[uc.id] ?? [],
    };
  });
  return {
    v: VALUE_STORY_VERSION,
    created: input.created,
    customerName: input.customerName || '',
    deal: {
      acv: input.deal.acv,
      winRate: input.deal.winRate,
      contractEstimate: input.deal.contractEstimate,
      currentAnnualPipeline: input.deal.currentAnnualPipeline,
    },
    rollup: {
      gross: rollup.gross,
      program: rollup.program,
      attributed: rollup.attributed,
      pipelineGross: rollup.pipelineGross,
      pipeline: rollup.pipeline,
      roi: rollup.roi,
    },
    useCases: entries,
  };
}
